import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../lib/utils';
import DepartmentRow from './DepartmentRow';

const TABLE_COLUMNS = [
  { id: 'icon', label: '', width: 'w-16', sortable: false },
  { id: 'title', label: 'Department', width: 'w-40', sortable: true },
  { id: 'heads', label: 'Heads', width: 'flex-1', sortable: false },
  { id: 'employeesCount', label: 'Employees', width: 'w-28', sortable: true, align: 'right' },
  { id: 'actions', label: '', width: 'w-20', sortable: false },
];

function DepartmentsTable({
  departmentsData,
  isLoading,
  totalCount,
  currentPage,
  rowsPerPage,
  sortField,
  sortOrder,
  onSort,
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
}) {
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startIndex = currentPage * rowsPerPage + 1;
  const endIndex = Math.min((currentPage + 1) * rowsPerPage, totalCount);

  const getSortIcon = (columnId) => {
    if (sortField !== columnId) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const handleSuccess = (message) => {
    setSuccessMessage(message || 'Changes saved successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
    onRefresh();
  };

  const handleError = (message) => {
    setErrorMessage(message || 'An error occurred');
    setTimeout(() => setErrorMessage(null), 5000);
  };

  return (
    <div>
      {/* Table Header */}
      <div
        className="flex items-center gap-4 px-4 py-3 border-b bg-muted/30"
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        {TABLE_COLUMNS.map((column) => (
          <div
            key={column.id}
            className={cn(
              'text-sm font-medium text-muted-foreground',
              column.width,
              column.align === 'right' && 'text-right',
            )}
          >
            {column.sortable ? (
              <button
                type="button"
                onClick={() => onSort(column.id)}
                className={cn(
                  'flex items-center gap-1 hover:text-foreground transition-colors',
                  column.align === 'right' && 'ml-auto',
                )}
              >
                {column.label}
                {getSortIcon(column.id)}
              </button>
            ) : (
              column.label
            )}
          </div>
        ))}
      </div>

      {/* Table Body */}
      <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
        {isLoading &&
          Array.from({ length: 5 }).map((_, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="flex items-center gap-4 px-4 py-4"
            >
              <div className="w-16">
                <div className="h-10 w-10 rounded-lg bg-muted animate-pulse" />
              </div>
              <div className="w-40">
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              </div>
              <div className="flex-1">
                <div className="flex gap-2">
                  <div className="h-6 w-32 bg-muted animate-pulse rounded-full" />
                  <div className="h-6 w-32 bg-muted animate-pulse rounded-full" />
                </div>
              </div>
              <div className="w-28 text-right">
                <div className="h-4 w-8 bg-muted animate-pulse rounded ml-auto" />
              </div>
              <div className="w-20">
                <div className="h-8 w-8 bg-muted animate-pulse rounded-full ml-auto" />
              </div>
            </div>
          ))}

        {!isLoading && departmentsData.length === 0 && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            No departments found
          </div>
        )}

        {!isLoading &&
          departmentsData.length > 0 &&
          departmentsData.map((department) => (
            <DepartmentRow
              key={department._id}
              department={department}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          ))}
      </div>

      {/* Pagination */}
      <div
        className="flex items-center justify-between px-4 py-3 border-t"
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            className={cn(
              'px-2 py-1 rounded border bg-background text-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
            style={{ borderColor: 'hsl(var(--border))' }}
          >
            {[5, 10, 25, 50].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            {totalCount > 0 ? `${startIndex}-${endIndex} of ${totalCount}` : '0 results'}
          </span>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-50">
          {successMessage}
          <button type="button" onClick={() => setSuccessMessage(null)} className="ml-2 font-bold">
            ×
          </button>
        </div>
      )}

      {/* Error Toast */}
      {errorMessage && (
        <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg z-50">
          {errorMessage}
          <button type="button" onClick={() => setErrorMessage(null)} className="ml-2 font-bold">
            ×
          </button>
        </div>
      )}
    </div>
  );
}

DepartmentsTable.propTypes = {
  departmentsData: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      employeesCount: PropTypes.number.isRequired,
      heads: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  totalCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  sortField: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default DepartmentsTable;
